import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  LogIn,
  LogOut,
  Building2,
  FileText,
  BarChart3,
  Eye,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { PublicLayout } from '@/components/PublicLayout';
import {
  formatCurrency,
  formatDate,
  groupVociByCategoria,
  calcScostamento,
  type Analisi,
  type VoceSpesa,
  type AnalisiKPI,
  type StatoPatrimoniale,
  type AnalisiCondomino,
  type InfoCondominio,
} from '@/lib/index';

interface SessioneCondomino {
  id: string;
  condominio_id: string;
  nome_condomino: string;
  username: string;
}

interface AnalisiExtended extends Analisi {
  info_condominio?: InfoCondominio;
  saldo?: number;
}

export default function PortaleCondomini() {
  const [sessioneCondomino, setSessioneCondomino] = useState<SessioneCondomino | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [analisiList, setAnalisiList] = useState<AnalisiExtended[]>([]);
  const [selectedAnalisi, setSelectedAnalisi] = useState<AnalisiExtended | null>(null);
  const [voci, setVoci] = useState<VoceSpesa[]>([]);
  const [condomini, setCondomini] = useState<AnalisiCondomino[]>([]);
  const [statoPatrimoniale, setStatoPatrimoniale] = useState<StatoPatrimoniale[]>([]);
  const [kpi, setKpi] = useState<AnalisiKPI[]>([]);
  const [loadingAnalisi, setLoadingAnalisi] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [activeTab, setActiveTab] = useState<'panoramica' | 'spese' | 'grafici' | 'stato' | 'condomini' | 'nota'>('panoramica');

  useEffect(() => {
    const saved = sessionStorage.getItem('condomino_session');
    if (saved) {
      try {
        const s = JSON.parse(saved) as SessioneCondomino;
        setSessioneCondomino(s);
      } catch {
        sessionStorage.removeItem('condomino_session');
      }
    }
  }, []);

  useEffect(() => {
    if (!sessioneCondomino) return;

    const fetchAnalisi = async () => {
      setLoadingAnalisi(true);
      try {
        await supabase
          .from('accessi_condomini')
          .update({ ultimo_accesso: new Date().toISOString() })
          .eq('id', sessioneCondomino.id);

        const { data, error } = await supabase
          .from('analisi')
          .select('*')
          .eq('condominio_id', sessioneCondomino.condominio_id)
          .eq('stato', 'completata')
          .order('anno', { ascending: false });

        if (error) {
          toast.error('Errore nel caricamento delle analisi');
        } else {
          setAnalisiList(data || []);
        }
      } catch {
        toast.error('Errore di connessione');
      } finally {
        setLoadingAnalisi(false);
      }
    };

    fetchAnalisi();
  }, [sessioneCondomino]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const { data, error } = await supabase
        .from('accessi_condomini')
        .select('*')
        .eq('username', username.trim().toLowerCase())
        .eq('password', password)
        .eq('attivo', true)
        .single();

      if (error || !data) {
        setLoginError('Username o password non corretti, oppure accesso disabilitato.');
        return;
      }

      const sessione: SessioneCondomino = {
        id: data.id,
        condominio_id: data.condominio_id,
        nome_condomino: data.nome_condomino,
        username: username.trim().toLowerCase(),
      };
      sessionStorage.setItem('condomino_session', JSON.stringify(sessione));
      setSessioneCondomino(sessione);
      toast.success(`Benvenuto, ${sessione.nome_condomino}!`);
    } catch (err) {
      setLoginError(`Errore: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('condomino_session');
    setSessioneCondomino(null);
    setAnalisiList([]);
    setSelectedAnalisi(null);
    setUsername('');
    setPassword('');
  };

  const handleOpenAnalisi = async (analisi: AnalisiExtended) => {
    setLoadingDetail(true);
    setActiveTab('panoramica');
    try {
      const [vociRes, condRes, statoRes, kpiRes] = await Promise.all([
        supabase.from('voci_spesa').select('*').eq('analisi_id', analisi.id).order('categoria'),
        supabase.from('analisi_condomini').select('*').eq('analisi_id', analisi.id).order('nominativo'),
        supabase.from('analisi_stato_patrimoniale').select('*').eq('analisi_id', analisi.id).order('ordine'),
        supabase.from('analisi_kpi').select('*').eq('analisi_id', analisi.id).order('ordine'),
      ]);
      setVoci(vociRes.data || []);
      setCondomini(condRes.data || []);
      setStatoPatrimoniale(statoRes.data || []);
      setKpi(kpiRes.data || []);
      setSelectedAnalisi(analisi);
    } catch {
      toast.error('Errore nel caricamento del dettaglio');
    } finally {
      setLoadingDetail(false);
    }
  };

  if (!sessioneCondomino) {
    return (
      <PublicLayout>
        <div className="min-h-screen bg-sidebar flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-md"
          >
            <Card className="shadow-2xl border-sidebar-border bg-sidebar-accent">
              <CardHeader className="text-center pb-2">
                <div className="flex justify-center mb-4">
                  <img
                    src="/images/Logo Egim.jpg"
                    alt="EGIM SM"
                    className="h-16 w-16 rounded-full object-contain"
                  />
                </div>
                <p className="text-primary font-semibold tracking-widest text-xs uppercase">EGIM SM</p>
                <CardTitle className="text-sidebar-foreground text-2xl mt-2">Portale Condòmini</CardTitle>
                <p className="text-sidebar-foreground/50 text-sm">
                  Accedi per consultare i rendiconti del tuo condominio
                </p>
              </CardHeader>
              <CardContent className="pt-4">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-sidebar-foreground/70">Username</Label>
                    <Input
                      type="text"
                      placeholder="il tuo username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="bg-sidebar border-sidebar-border text-sidebar-foreground placeholder:text-sidebar-foreground/30"
                      required
                      autoComplete="username"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sidebar-foreground/70">Password</Label>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-sidebar border-sidebar-border text-sidebar-foreground placeholder:text-sidebar-foreground/30"
                      required
                      autoComplete="current-password"
                    />
                  </div>
                  {loginError && (
                    <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-3">
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      {loginError}
                    </div>
                  )}
                  <Button type="submit" className="w-full gap-2" disabled={loginLoading}>
                    {loginLoading ? (
                      <span className="animate-spin h-4 w-4 border-2 border-primary-foreground border-t-transparent rounded-full" />
                    ) : (
                      <LogIn className="h-4 w-4" />
                    )}
                    {loginLoading ? 'Accesso...' : 'Accedi'}
                  </Button>
                </form>
                <Separator className="my-4 bg-sidebar-border" />
                <p className="text-xs text-sidebar-foreground/30 text-center">
                  Area riservata condòmini — Le credenziali sono fornite dall'amministratore
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </PublicLayout>
    );
  }

  if (selectedAnalisi) {
    const macroCategorie = groupVociByCategoria(voci);
    const hasPreventivo = (selectedAnalisi.totale_preventivo || 0) > 0;
    const scostamento = calcScostamento(selectedAnalisi.totale_uscite, selectedAnalisi.totale_preventivo);
    const scostamentoTipo =
      scostamento.euro > 0 ? 'sforamento' : scostamento.euro < 0 ? 'risparmio' : 'neutro';

    const attivita = statoPatrimoniale.filter((s) => s.tipo === 'attivita');
    const passivita = statoPatrimoniale.filter((s) => s.tipo === 'passivita');
    const totaleAttivita = attivita.reduce((sum, a) => sum + a.importo, 0);
    const totalePassivita = passivita.reduce((sum, p) => sum + p.importo, 0);

    const morosi = condomini.filter((c) => c.saldo_finale > 0);
    const saldo = selectedAnalisi.totale_entrate - selectedAnalisi.totale_uscite;

    return (
      <PublicLayout>
        <div className="min-h-screen bg-background">
          <header className="bg-sidebar text-sidebar-foreground px-4 py-3 flex items-center justify-between sticky top-0 z-50 shadow-lg print:hidden">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                className="text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent"
                onClick={() => setSelectedAnalisi(null)}
              >
                ← Torna alle analisi
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent"
                onClick={() => window.print()}
              >
                🖨️ Esporta PDF
              </Button>
            </div>
          </header>

          <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
            <div className="rounded-xl bg-sidebar text-sidebar-foreground p-6 shadow-lg">
              <h1 className="text-2xl font-bold">{selectedAnalisi.titolo}</h1>
              <p className="text-sidebar-foreground/60 text-sm mt-1">Esercizio {selectedAnalisi.anno}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                <div className="bg-sidebar-accent rounded-lg p-3">
                  <p className="text-xs text-sidebar-foreground/50 uppercase tracking-wider mb-1">Preventivo</p>
                  <p className="text-xl font-mono font-bold">
                    {formatCurrency(selectedAnalisi.totale_preventivo || 0)}
                  </p>
                </div>
                <div className="bg-sidebar-accent rounded-lg p-3">
                  <p className="text-xs text-sidebar-foreground/50 uppercase tracking-wider mb-1">Consuntivo</p>
                  <p className="text-xl font-mono font-bold">{formatCurrency(selectedAnalisi.totale_uscite)}</p>
                </div>
                <div
                  className={`rounded-lg p-3 ${
                    scostamentoTipo === 'sforamento'
                      ? 'bg-destructive/20'
                      : scostamentoTipo === 'risparmio'
                      ? 'bg-success/20'
                      : 'bg-sidebar-accent'
                  }`}
                >
                  <p className="text-xs text-sidebar-foreground/50 uppercase tracking-wider mb-1">Scostamento</p>
                  <p className="text-xl font-mono font-bold flex items-center gap-2">
                    {scostamentoTipo === 'sforamento' ? (
                      <TrendingUp className="h-5 w-5 text-destructive" />
                    ) : scostamentoTipo === 'risparmio' ? (
                      <TrendingDown className="h-5 w-5 text-success" />
                    ) : (
                      <Minus className="h-5 w-5" />
                    )}
                    {formatCurrency(scostamento.euro)}
                  </p>
                </div>
              </div>
            </div>

            <div className="border-b border-border print:hidden">
              <div className="flex gap-1 overflow-x-auto">
                {[
                  { id: 'panoramica', label: 'Panoramica', icon: Eye },
                  { id: 'spese', label: 'Dettaglio Spese', icon: FileText },
                  { id: 'stato', label: 'Stato Patrimoniale', icon: Building2 },
                  { id: 'condomini', label: 'Condòmini', icon: Building2 },
                  { id: 'nota', label: 'Nota Integrativa', icon: FileText },
                ].map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as typeof activeTab)}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
                        activeTab === tab.id
                          ? 'border-primary text-primary'
                          : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {activeTab === 'panoramica' && (
              <div className="space-y-6">
                {selectedAnalisi.info_condominio && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Informazioni Condominio</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        {selectedAnalisi.info_condominio.nome && (
                          <div>
                            <span className="text-muted-foreground">Nome:</span>{' '}
                            <strong>{selectedAnalisi.info_condominio.nome}</strong>
                          </div>
                        )}
                        {selectedAnalisi.info_condominio.indirizzo && (
                          <div>
                            <span className="text-muted-foreground">Indirizzo:</span>{' '}
                            <strong>{selectedAnalisi.info_condominio.indirizzo}</strong>
                          </div>
                        )}
                        {selectedAnalisi.info_condominio.citta && (
                          <div>
                            <span className="text-muted-foreground">Città:</span>{' '}
                            <strong>{selectedAnalisi.info_condominio.citta}</strong>
                          </div>
                        )}
                        {selectedAnalisi.info_condominio.cf && (
                          <div>
                            <span className="text-muted-foreground">CF:</span>{' '}
                            <strong>{selectedAnalisi.info_condominio.cf}</strong>
                          </div>
                        )}
                        {selectedAnalisi.info_condominio.amministratore && (
                          <div>
                            <span className="text-muted-foreground">Amministratore:</span>{' '}
                            <strong>{selectedAnalisi.info_condominio.amministratore}</strong>
                          </div>
                        )}
                        {selectedAnalisi.info_condominio.periodo && (
                          <div>
                            <span className="text-muted-foreground">Periodo:</span>{' '}
                            <strong>{selectedAnalisi.info_condominio.periodo}</strong>
                          </div>
                        )}
                        {selectedAnalisi.info_condominio.n_unita && (
                          <div>
                            <span className="text-muted-foreground">N. Unità:</span>{' '}
                            <strong>{selectedAnalisi.info_condominio.n_unita}</strong>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {kpi.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {kpi.map((k) => (
                      <Card
                        key={k.id}
                        className={`border-l-4 ${
                          k.stato === 'critico'
                            ? 'border-l-destructive'
                            : k.stato === 'attenzione'
                            ? 'border-l-warning'
                            : 'border-l-success'
                        }`}
                      >
                        <CardContent className="pt-6">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{k.titolo}</p>
                          <p className="text-2xl font-bold font-mono">
                            {k.valore} {k.unita}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Totale Uscite</p>
                          <p className="text-2xl font-bold font-mono mt-1">
                            {formatCurrency(selectedAnalisi.totale_uscite)}
                          </p>
                        </div>
                        <div className="p-3 rounded-full bg-destructive/10">
                          <TrendingUp className="h-6 w-6 text-destructive" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Totale Entrate</p>
                          <p className="text-2xl font-bold font-mono mt-1">
                            {formatCurrency(selectedAnalisi.totale_entrate)}
                          </p>
                        </div>
                        <div className="p-3 rounded-full bg-success/10">
                          <TrendingDown className="h-6 w-6 text-success" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Saldo</p>
                          <p
                            className={`text-2xl font-bold font-mono mt-1 ${
                              saldo < 0 ? 'text-destructive' : saldo > 0 ? 'text-success' : ''
                            }`}
                          >
                            {formatCurrency(saldo)}
                          </p>
                        </div>
                        <div
                          className={`p-3 rounded-full ${
                            saldo < 0 ? 'bg-destructive/10' : saldo > 0 ? 'bg-success/10' : 'bg-muted'
                          }`}
                        >
                          {saldo < 0 ? (
                            <AlertCircle className="h-6 w-6 text-destructive" />
                          ) : saldo > 0 ? (
                            <CheckCircle className="h-6 w-6 text-success" />
                          ) : (
                            <Minus className="h-6 w-6" />
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {activeTab === 'spese' && voci.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Dettaglio Spese per Categoria</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead>Categoria</TableHead>
                          <TableHead>Descrizione</TableHead>
                          <TableHead className="text-right">Consuntivo €</TableHead>
                          {hasPreventivo && <TableHead className="text-right">Preventivo €</TableHead>}
                          {hasPreventivo && <TableHead className="text-right">Scostamento €</TableHead>}
                          {hasPreventivo && <TableHead className="text-right">Scostamento %</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {macroCategorie.map((macro) => (
                          <>
                            <TableRow
                              key={`m-${macro.nome}`}
                              className="bg-sidebar/5 border-t-2 border-sidebar/20"
                            >
                              <TableCell colSpan={2} className="font-bold text-sidebar text-sm py-2">
                                {macro.nome}
                              </TableCell>
                              <TableCell className="text-right font-mono font-bold text-sm py-2">
                                {formatCurrency(macro.totale_consuntivo)}
                              </TableCell>
                              {hasPreventivo && (
                                <TableCell className="text-right font-mono font-bold text-sm py-2">
                                  {formatCurrency(macro.totale_preventivo)}
                                </TableCell>
                              )}
                              {hasPreventivo && (
                                <TableCell
                                  className={`text-right font-mono font-bold text-sm py-2 ${
                                    Math.abs(macro.scostamento_pct || 0) > 10
                                      ? macro.scostamento_euro > 0
                                        ? 'text-destructive'
                                        : 'text-success'
                                      : ''
                                  }`}
                                >
                                  {macro.scostamento_euro > 0 ? '+' : ''}
                                  {formatCurrency(macro.scostamento_euro)}
                                </TableCell>
                              )}
                              {hasPreventivo && (
                                <TableCell
                                  className={`text-right font-mono font-bold text-sm py-2 ${
                                    Math.abs(macro.scostamento_pct || 0) > 10
                                      ? macro.scostamento_euro > 0
                                        ? 'text-destructive'
                                        : 'text-success'
                                      : ''
                                  }`}
                                >
                                  {macro.scostamento_pct !== null
                                    ? `${macro.scostamento_euro > 0 ? '+' : ''}${macro.scostamento_pct.toFixed(1)}%`
                                    : 'N/D'}
                                </TableCell>
                              )}
                            </TableRow>
                            {macro.voci.map((voce) => {
                              const sc = calcScostamento(voce.importo_consuntivo, voce.importo_preventivo);
                              const isHighScostamento = Math.abs(sc.percentuale || 0) > 10;
                              const scTipo = sc.euro > 0 ? 'sforamento' : sc.euro < 0 ? 'risparmio' : 'neutro';
                              return (
                                <TableRow key={voce.id} className="hover:bg-muted/30">
                                  <TableCell className="text-xs text-muted-foreground pl-8">
                                    {voce.categoria}
                                  </TableCell>
                                  <TableCell className="text-sm">{voce.descrizione}</TableCell>
                                  <TableCell className="text-right font-mono text-sm">
                                    {formatCurrency(voce.importo_consuntivo)}
                                  </TableCell>
                                  {hasPreventivo && (
                                    <TableCell className="text-right font-mono text-sm text-muted-foreground">
                                      {formatCurrency(voce.importo_preventivo)}
                                    </TableCell>
                                  )}
                                  {hasPreventivo && (
                                    <TableCell
                                      className={`text-right font-mono text-sm ${
                                        isHighScostamento
                                          ? scTipo === 'sforamento'
                                            ? 'text-destructive'
                                            : scTipo === 'risparmio'
                                            ? 'text-success'
                                            : ''
                                          : ''
                                      }`}
                                    >
                                      {scTipo !== 'neutro' && (scTipo === 'sforamento' ? '+' : '')}
                                      {formatCurrency(sc.euro)}
                                    </TableCell>
                                  )}
                                  {hasPreventivo && (
                                    <TableCell
                                      className={`text-right font-mono text-sm ${
                                        isHighScostamento
                                          ? scTipo === 'sforamento'
                                            ? 'text-destructive'
                                            : scTipo === 'risparmio'
                                            ? 'text-success'
                                            : ''
                                          : ''
                                      }`}
                                    >
                                      {sc.percentuale !== null
                                        ? `${scTipo === 'sforamento' ? '+' : ''}${sc.percentuale.toFixed(1)}%`
                                        : 'N/D'}
                                    </TableCell>
                                  )}
                                </TableRow>
                              );
                            })}
                          </>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {activeTab === 'stato' && statoPatrimoniale.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Stato Patrimoniale</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                    <div className="border-r border-border">
                      <div className="bg-muted/50 px-4 py-2 font-semibold">Attività</div>
                      <Table>
                        <TableBody>
                          {attivita.map((a) => (
                            <TableRow key={a.id}>
                              <TableCell className="text-sm">{a.descrizione}</TableCell>
                              <TableCell className="text-right font-mono text-sm">
                                {formatCurrency(a.importo)}
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-muted/50 font-bold">
                            <TableCell>Totale Attività</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(totaleAttivita)}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                    <div>
                      <div className="bg-muted/50 px-4 py-2 font-semibold">Passività</div>
                      <Table>
                        <TableBody>
                          {passivita.map((p) => (
                            <TableRow key={p.id}>
                              <TableCell className="text-sm">{p.descrizione}</TableCell>
                              <TableCell className="text-right font-mono text-sm">
                                {formatCurrency(p.importo)}
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-muted/50 font-bold">
                            <TableCell>Totale Passività</TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(totalePassivita)}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {activeTab === 'condomini' && condomini.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Situazione Condòmini</span>
                    {morosi.length > 0 && (
                      <Badge variant="destructive" className="text-xs">
                        {morosi.length} moroso/i
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead>Nominativo</TableHead>
                          <TableHead className="text-right">Piano/Interno</TableHead>
                          <TableHead className="text-right">Quota Ordinaria</TableHead>
                          <TableHead className="text-right">Quota Riscaldamento</TableHead>
                          <TableHead className="text-right">Tot. Consuntivo</TableHead>
                          <TableHead className="text-right">Saldo Prec.</TableHead>
                          <TableHead className="text-right">Versamenti</TableHead>
                          <TableHead className="text-right">Saldo Finale</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {condomini.map((c) => (
                          <TableRow key={c.id} className={c.saldo_finale > 0 ? 'bg-destructive/10' : ''}>
                            <TableCell className="font-medium">
                              {c.nominativo}
                              {c.saldo_finale > 0 && (
                                <Badge variant="destructive" className="ml-2 text-xs">
                                  Moroso
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm">{c.piano_interno || '-'}</TableCell>
                            <TableCell className="text-right font-mono text-sm">
                              {formatCurrency((c as unknown as Record<string, number>).quota_ordinaria ?? 0)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm">
                              {formatCurrency(c.totale_consuntivo ?? (c as unknown as Record<string, number>).tot_consuntivo ?? 0)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm">
                              {formatCurrency(c.saldo_ordinario ?? (c as unknown as Record<string, number>).saldo_prec ?? 0)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm">
                              {formatCurrency(c.vers_gest_corrente ?? (c as unknown as Record<string, number>).versamenti ?? 0)}
                            </TableCell>
                            <TableCell
                              className={`text-right font-mono text-sm font-bold ${
                                c.saldo_finale > 0
                                  ? 'text-destructive'
                                  : c.saldo_finale < 0
                                  ? 'text-success'
                                  : ''
                              }`}
                            >
                              {formatCurrency(c.saldo_finale)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {activeTab === 'nota' && selectedAnalisi.nota_sintetica && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary" />
                    Nota Integrativa
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-sm max-w-none">
                    {selectedAnalisi.nota_sintetica.split('\n\n').map((section, i) => {
                      const lines = section.split('\n');
                      const isHeading = lines[0].match(/^[A-Z0-9][.)\s]/);
                      if (isHeading) {
                        return (
                          <div key={i} className="mb-6">
                            <h3 className="text-base font-bold text-foreground mb-2">{lines[0]}</h3>
                            {lines.slice(1).map((line, j) => (
                              <p key={j} className="text-sm leading-relaxed text-foreground/90 mb-2">
                                {line}
                              </p>
                            ))}
                          </div>
                        );
                      }
                      return (
                        <p key={i} className="text-sm leading-relaxed text-foreground/90 mb-4">
                          {section}
                        </p>
                      );
                    })}
                  </div>
                  <Separator className="my-6" />
                  <p className="text-xs text-muted-foreground">
                    Analisi generata il {formatDate(selectedAnalisi.created_at)} — EgimConsuntivi © 2026
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="min-h-screen bg-background">
        <header className="bg-sidebar text-sidebar-foreground px-4 py-3 flex items-center justify-between sticky top-0 z-50 shadow-lg">
          <div className="flex items-center gap-3">
            <img src="/images/Logo Egim.jpg" alt="EGIM" className="h-8 w-8 rounded-full object-contain" />
            <div>
              <p className="font-semibold text-sm">Portale Condòmini — EGIM SM</p>
              <p className="text-sidebar-foreground/50 text-xs">{sessioneCondomino.nome_condomino}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent gap-2"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4" /> Esci
          </Button>
        </header>

        <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-xl bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 p-6"
          >
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-primary/20">
                <Building2 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Benvenuto, {sessioneCondomino.nome_condomino}</h1>
                <p className="text-muted-foreground text-sm">
                  Di seguito trovi le analisi disponibili per il tuo condominio
                </p>
              </div>
            </div>
          </motion.div>

          <div className="space-y-3">
            <h2 className="font-semibold flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              Rendiconti Disponibili ({analisiList.length})
            </h2>

            {loadingAnalisi ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
              </div>
            ) : analisiList.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-20" />
                  <p>Nessuna analisi disponibile al momento.</p>
                  <p className="text-xs mt-2">L'amministratore caricherà i rendiconti appena disponibili.</p>
                </CardContent>
              </Card>
            ) : (
              analisiList.map((a, i) => (
                <motion.div
                  key={a.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Card
                    className="cursor-pointer hover:border-primary/40 transition-all hover:shadow-md group"
                    onClick={() => handleOpenAnalisi(a)}
                  >
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                            <FileText className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-sm">{a.titolo}</h3>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <Badge variant="outline" className="text-xs">
                                Anno {a.anno}
                              </Badge>
                              <Badge
                                variant={a.tipo_gestione === 'ordinaria' ? 'default' : 'secondary'}
                                className="text-xs"
                              >
                                {a.tipo_gestione}
                              </Badge>
                              <span className="text-xs text-muted-foreground">{formatDate(a.created_at)}</span>
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                              <span>
                                Consuntivo:{' '}
                                <strong className="text-foreground">{formatCurrency(a.totale_uscite)}</strong>
                              </span>
                              {(a.totale_preventivo || 0) > 0 && (
                                <span>
                                  Preventivo:{' '}
                                  <strong className="text-foreground">
                                    {formatCurrency(a.totale_preventivo || 0)}
                                  </strong>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="text-xs bg-success/10 text-success border-success/30">
                            <CheckCircle className="h-3 w-3 mr-1" /> Disponibile
                          </Badge>
                          <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))
            )}
          </div>
        </div>

        {loadingDetail && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-card rounded-xl p-8 flex flex-col items-center gap-4 shadow-2xl">
              <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full" />
              <p className="text-sm">Caricamento analisi...</p>
            </div>
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
